/**
 * TCF v2 reader. Consent requires the kueez vendor plus purpose 1 (store and/or
 * access information on a device). If `gdprApplies` is false, the CMP says GDPR
 * does not apply and consent is treated as granted.
 *
 * Falls through to the consumer (engine) if no `__tcfapi` resolves within
 * `TIMEOUT_MS`, so a missing/broken CMP doesn't deadlock init.
 */

import { warn } from '../../util/log';
import { readTcfApi, type TcfData } from './cmpApis';

/** Kueez's TCF Global Vendor List id. */
const KUEEZ_VENDOR_ID = '1165';
const CONTENT_PURPOSE_ID = '1';
const TIMEOUT_MS = 8000;

export type TcfResult =
  | { kind: 'granted'; tcString?: string }
  | { kind: 'denied'; tcString?: string }
  | { kind: 'unavailable' };

export function waitForTcf(timeoutMs: number = TIMEOUT_MS): Promise<TcfResult> {
  return new Promise<TcfResult>((resolve) => {
    const api = readTcfApi();
    if (!api) {
      resolve({ kind: 'unavailable' });
      return;
    }

    let settled = false;
    const settle = (result: TcfResult) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };

    const timer = setTimeout(() => settle({ kind: 'unavailable' }), timeoutMs);

    try {
      api('addEventListener', 2, (data, success) => {
        if (!success || !data) return;
        if (data.eventStatus === 'tcloaded' || data.eventStatus === 'useractioncomplete') {
          clearTimeout(timer);
          settle(toTcfResult(data));
        }
      });
    } catch (err) {
      warn('tcf addEventListener threw', err);
      clearTimeout(timer);
      settle({ kind: 'unavailable' });
    }
  });
}

export function hasPurposeConsent(data: TcfData): boolean {
  return data.vendor?.consents?.[KUEEZ_VENDOR_ID] === true
    && data.purpose?.consents?.[CONTENT_PURPOSE_ID] === true;
}

/** Map TCF payload to tracking consent (yoto `hasTCFv2Consent` semantics). */
export function consentStatusFromTcfData(data: TcfData): 'granted' | 'denied' | null {
  const result = toTcfResult(data);
  return result.kind === 'unavailable' ? null : result.kind;
}

export function subscribeTcfConsent(
  onChange: (status: 'granted' | 'denied') => void,
  onCmpEvent?: () => void,
): () => void {
  const api = readTcfApi();
  if (!api) return () => {};

  let listenerId: number | undefined;
  try {
    api('addEventListener', 2, (data, success) => {
      if (!success || !data) return;
      if (typeof data.listenerId === 'number') listenerId = data.listenerId;
      const status = consentStatusFromTcfData(data);
      if (status !== null) onChange(status);
      if (
        onCmpEvent
        && (data.eventStatus === 'tcloaded' || data.eventStatus === 'useractioncomplete')
      ) {
        onCmpEvent();
      }
    });
  } catch (err) {
    warn('tcf subscribe threw', err);
  }

  return () => {
    if (listenerId === undefined) return;
    try {
      api('removeEventListener', 2, () => {}, listenerId);
    } catch (err) {
      warn('tcf unsubscribe threw', err);
    }
  };
}

function toTcfResult(data: TcfData): TcfResult {
  if (data.gdprApplies === false) {
    return { kind: 'granted', tcString: data.tcString };
  }
  return { kind: hasPurposeConsent(data) ? 'granted' : 'denied', tcString: data.tcString };
}
